import { useEffect, useRef, useState } from 'react';

import { apiClient } from '../../api-client';

const PORTAL_ID = import.meta.env.VITE_HUBSPOT_PORTAL_ID as string;
const FORM_GUID = import.meta.env.VITE_HUBSPOT_FORM_GUID as string;
const SCRIPT_SRC = `https://js.hsforms.net/forms/embed/${PORTAL_ID}.js`;

interface HubSpotFormProps {
  className?: string;
}

const HubSpotForm = ({ className = '' }: HubSpotFormProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!PORTAL_ID || !FORM_GUID) {
      console.error(
        'HubSpot form env vars missing (VITE_HUBSPOT_PORTAL_ID / VITE_HUBSPOT_FORM_GUID)',
      );
      setLoading(false);
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === 'hsFormCallback' &&
        event.data?.eventName === 'onFormSubmitted' &&
        event.data?.id === FORM_GUID
      ) {
        const vals: Record<string, string> =
          event.data?.data?.submissionValues ?? {};

        apiClient.community
          .communitySignup({
            first_name: vals['firstname'] ?? '',
            last_name: vals['lastname'] ?? '',
            email: vals['email'] ?? '',
            phone: vals['phone'] || undefined,
            beta_tester: vals['beta_tester_konnect_better'] === 'true',
          })
          .catch((err) => {
            console.error('Backend community signup (dual-write) failed:', err);
          });
      }
    };

    window.addEventListener('message', handleMessage);

    const observer = new MutationObserver(() => {
      if (containerRef.current?.querySelector('iframe, form')) {
        setLoading(false);
        observer.disconnect();
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.defer = true;
      script.onerror = () => {
        console.error('Failed to load HubSpot forms script');
        setLoading(false);
      };
      document.head.appendChild(script);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      observer.disconnect();
    };
  }, []);

  return (
    <div className={className}>
      {loading && (
        <div className="flex justify-center items-center py-12">
          <svg
            className="animate-spin h-6 w-6 text-secondary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}
      <div
        ref={containerRef}
        className="hs-form-frame"
        data-region="na1"
        data-form-id={FORM_GUID}
        data-portal-id={PORTAL_ID}
      />
    </div>
  );
};

export default HubSpotForm;
